import { getConnectors } from "@/components/connectors/api.ts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { cn } from "@/lib/utils.ts";
import { isOkthxCalendarId } from "@/types/calendarEvent.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronUp, Palette } from "lucide-react";
import { useMemo, useState } from "react";
import { type Calendar, getCalendars, setCalendarColor } from "./api.ts";
import {
  CALENDAR_COLOR_CHOICES,
  useCalendarColors,
  useRegisteredCalendarColors,
} from "./calendarColors.ts";

export const CalendarList = ({
  hiddenCalendarIds = new Set<string>(),
  onToggleCalendar,
}: {
  hiddenCalendarIds?: Set<string>;
  onToggleCalendar?: (calendarId: string) => void;
}) => {
  const { data: calendars } = useQuery({
    queryKey: ["calendars"],
    queryFn: getCalendars,
    staleTime: 5 * 60 * 1000,
  });

  const { data: connectorsData } = useQuery({
    queryKey: ["connectors"],
    queryFn: getConnectors,
    staleTime: 5 * 60 * 1000,
  });

  useRegisteredCalendarColors(calendars);

  const connectors = connectorsData?.connectors ?? [];

  // Group calendars by account, sorted: primary account first, then by email
  const calendarGroups = useMemo(() => {
    if (!calendars) return [];

    const grouped = new Map<number | undefined, Calendar[]>();
    for (const cal of calendars) {
      const key = cal.connectorId;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(cal);
    }

    return [...grouped.entries()]
      .map(([accountId, cals]) => {
        const connector = connectors.find((a) => a.id === accountId);
        // A connectorless group is the okthx built-in calendar (Reminders). It
        // belongs to no provider account, so it is headed by the product rather
        // than by an address — every other group is titled by the account its
        // calendars come from, and a headerless list read as one of them having
        // come loose.
        const isConnectorless = accountId === undefined;
        return {
          accountId,
          label: isConnectorless ? "Okthx" : (connector?.displayName ?? "Unknown account"),
          provider: connector?.provider ?? "google",
          isLoginConnector: connector?.isLogin ?? false,
          isConnectorless,
          calendars: [...cals].sort((a, b) => {
            if (a.primary && !b.primary) return -1;
            if (!a.primary && b.primary) return 1;
            return (a.summary ?? "").localeCompare(b.summary ?? "");
          }),
        };
      })
      .sort((a, b) => {
        if (a.isLoginConnector && !b.isLoginConnector) return -1;
        if (!a.isLoginConnector && b.isLoginConnector) return 1;
        // Connectorless built-ins (Reminders) sort after provider accounts.
        if (a.isConnectorless && !b.isConnectorless) return 1;
        if (!a.isConnectorless && b.isConnectorless) return -1;
        return a.label.localeCompare(b.label);
      });
  }, [calendars, connectors]);

  if (!calendarGroups.length) return null;

  return (
    <div className="flex flex-col gap-4">
      {calendarGroups.map((group) => (
        <AccountCalendarGroup
          key={group.accountId ?? "okthx"}
          label={group.label}
          calendars={group.calendars}
          hiddenCalendarIds={hiddenCalendarIds}
          onToggleCalendar={onToggleCalendar}
        />
      ))}
    </div>
  );
};

const AccountCalendarGroup = ({
  label,
  calendars,
  hiddenCalendarIds,
  onToggleCalendar,
}: {
  label: string;
  calendars: Calendar[];
  hiddenCalendarIds: Set<string>;
  onToggleCalendar?: (calendarId: string) => void;
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const items = (
    <div className="mt-1 flex flex-col gap-0.5">
      {calendars.map((calendar) => (
        <CalendarListItem
          key={calendar.calendarId}
          calendar={calendar}
          isVisible={!hiddenCalendarIds.has(calendar.calendarId)}
          onToggle={() => onToggleCalendar?.(calendar.calendarId)}
        />
      ))}
    </div>
  );

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="group flex w-full items-center gap-1.5 py-1 cursor-pointer transition-colors"
      >
        {/* The account name carries the group on its own; the provider icon
            repeated what each calendar row below already shows. */}
        <span className="min-w-0 truncate text-sm font-medium text-foreground">{label}</span>
        {/* Sits with the name rather than pinned to the column's right edge, so
            the affordance reads as belonging to the label it expands. */}
        <ChevronUp
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-all opacity-0 group-hover:opacity-100",
            collapsed && "rotate-180",
          )}
        />
      </button>

      {!collapsed && items}
    </div>
  );
};

/**
 * Colour swatches for an okthx calendar, which is the only kind whose colour the
 * user owns — a provider calendar's colour is set at the provider and read-only
 * here. Writes through the calendar-colour endpoint and invalidates the
 * calendars query, so the registry re-registers and every subscriber — this
 * swatch, the row's dot, the grid's chips — repaints without a reload.
 */
const CalendarColorPicker = ({ calendar }: { calendar: Calendar }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { getCalendarHex } = useCalendarColors();

  const mutation = useMutation({
    mutationFn: (color: string) => setCalendarColor(calendar.calendarId, color),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendars"] });
    },
    onError: () => errorToast("Couldn't change the calendar color"),
  });

  const current = getCalendarHex(calendar.calendarId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Change the ${calendar.summary ?? "calendar"} color`}
          data-statsig-name="calendar-color-trigger"
          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity shrink-0 p-1 rounded hover:bg-accent"
        >
          <Palette className="size-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-2">
        <div className="flex flex-wrap items-center gap-1.5 max-w-40">
          {CALENDAR_COLOR_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              aria-label={choice}
              disabled={mutation.isPending}
              onClick={() => {
                mutation.mutate(choice);
                setOpen(false);
              }}
              className={cn(
                "size-5 rounded-full transition-transform hover:scale-110",
                choice.toLowerCase() === current.toLowerCase() && "ring-2 ring-offset-1 ring-ring",
              )}
              style={{ backgroundColor: choice }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CalendarListItem = ({
  calendar,
  isVisible,
  onToggle,
}: {
  calendar: Calendar;
  isVisible: boolean;
  onToggle: () => void;
}) => {
  const { getCalendarHex } = useCalendarColors();
  const color = getCalendarHex(calendar.calendarId);
  const name = calendar.summary ?? calendar.calendarId;

  return (
    <div className="flex items-center gap-1 group rounded-md hover:bg-accent/50 transition-colors">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2.5 py-1.5 cursor-pointer w-full min-w-0 text-left"
      >
        <div
          className="size-4.5 rounded-full flex items-center justify-center shrink-0 transition-colors"
          style={{
            backgroundColor: isVisible ? color : "transparent",
            border: isVisible ? "none" : `2px solid ${color}`,
          }}
        >
          {isVisible && <Check className="size-3 text-white" strokeWidth={3} />}
        </div>
        <span
          className={cn(
            "text-sm truncate",
            isVisible ? "text-foreground/80" : "text-foreground/40",
          )}
        >
          {name}
        </span>
      </button>
      {/* Only okthx calendars are user-colourable, and none can be renamed or
          deleted — the colour swatch is the whole of what this row offers. */}
      {isOkthxCalendarId(calendar.calendarId) && <CalendarColorPicker calendar={calendar} />}
    </div>
  );
};
